import React, { useState } from "react";
import "./PackingDashboard.css";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import useTranslator from "../../../hooks/useTranslator";
import { getCurrentDate, makePriorLotNumber } from "../../../helpers/Utils";
import DashboardSection from "../../dashboard/components/DashboardSection";
import DashboardCard from "../../dashboard/components/DashboardCard";
import "../../dashboard/components/dashboard-sections.css";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import { CartIconSvg } from "../../../svgFiles/CartIconSvg";
import { ScaleIconSvg } from "../../../svgFiles/ScaleIconSvg";
import { BarChartIconSvg } from "../../../svgFiles/BarChartIconSvg";
import { SortingIconSvg } from "../../../svgFiles/SortingIconSvg";
import { useGetPackingAnalyticsQuery } from "../../dashboard/dashBoardApi";

const PackingDashboard = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(currentDate);
  const [isSingleDay, setIsSingleDay] = useState(true);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(currentDate));

  const MetricBox = ({ label, value, unit, theme = "orange" }) => (
    <div className={`pko-box pko-box-${theme}`}>
      <div className="pko-box-label">{label}</div>
      <div className="pko-box-value">{value ?? "—"}</div>
      {unit && <div className={`pko-box-unit pko-unit-${theme}`}>{unit}</div>}
    </div>
  );

  const formatValueWithUnit = (label, value) => {
    const displayValue = value ?? 0;
    const strValue = String(displayValue);
    if (/%/.test(label)) return /%/.test(strValue) ? strValue : `${strValue} %`;
    if (/per\s*kg|\/\s*kg/i.test(label)) return /per\s*kg/i.test(strValue) ? strValue : `${strValue} Per KG`;
    if (/\bkg\b|kilo|weight/i.test(label)) return /\bkg\b/i.test(strValue) ? strValue : `${strValue} KG`;
    return strValue;
  };

  const handleApplyRange = ({ startDate: start, endDate: end }) => {
    const single = start === end;
    setIsSingleDay(single);
    setStartDate(start);
    setEndDate(end);
    if (single) setLotNumber(makePriorLotNumber(start));
  };

  const { data: packingData } = useGetPackingAnalyticsQuery(
    {
      startDate,
      endDate,
      batchFrom: makePriorLotNumber(startDate),
      batchTo: makePriorLotNumber(endDate),
    },
    { skip: !startDate || !endDate }
  );

  const day = packingData?.data?.packing_day;
  const night = packingData?.data?.packing_night;

  const enteredCompletedTreysItems = [
    { label: "Cheeks Tray Completed", value: night?.cheeks_tray_completed ?? 0 },
    { label: "Tapita Tray Completed", value: night?.tapita_tray_completed ?? 0 },
    { label: "Strips Tray Completed", value: night?.strips_tray_completed ?? 0 },
    { label: "Total Cart Completed",  value: night?.total_cart_completed ?? 0 },
  ];

  const kgBaggedItems = [
    { label: "Cheeks KG Bagged", value: night?.cheeks_kg_bagged ?? 0 },
    { label: "Tapita KG Bagged", value: night?.tapita_kg_bagged ?? 0 },
    { label: "Strips KG Bagged", value: night?.strips_kg_bagged ?? 0 },
    { label: "Total KG Bagged",  value: night?.total_kg_bagged ?? 0 },
  ];

  const sortingItems = [
    { label: "Total Prime KG",   value: night?.total_prime_kg ?? 0 },
    { label: "Prime Cheeks KG",  value: night?.prime_cheeks_kg ?? 0 },
    { label: "Prime Strips KG",  value: night?.prime_strips_kg ?? 0 },
    { label: "Rework Cheeks KG", value: night?.rework_cheeks_kg ?? 0 },
    { label: "Rework Strips KG", value: night?.rework_strips_kg ?? 0 },
  ];

  const metricsItems = [
    { label: "Prime Cheeks %", value: night?.prime_cheeks_pct ?? 0 },
    { label: "Prime Strips %",  value: night?.prime_strips_pct ?? 0 },
    { label: "Total Prime %",   value: night?.total_prime_pct ?? 0 },
  ];

  return (
    <div>
      {/* ── Toolbar ───────────────────────────────────────────── */}
      <div className="packing_dashboard_toolbar mb-4 align-items-center">
        <h5 className="packing_dashboard_toolbar_title m-0">
          {_t("packing_overview", "Packing Overview")}
        </h5>
        <div className="packing_toolbar_right">
          {isSingleDay && (
            <div className="packing_filter_group">
              <label className="packing_filter_label">{_t("lot_number", "Lot Number")}</label>
              <div className="packing_lot_number_row">
                <div className="packing_lot_chip borderRedBtn cmn_btn btn-small">#{lotNumber}</div>
              </div>
            </div>
          )}
          <div className="packing_filter_group">
            <label htmlFor="packing-date-range-filter" className="packing_filter_label">
              {_t("date_range", "Date Range")}
            </label>
            {/* <DateRangeFilter setDate={setStartDate} onApplyRange={handleApplyRange} triggerId="packing-date-range-filter" /> */}
          </div>
        </div>
      </div>

      {/* ── Section 1 ─────────────────────────────────────────── */}
      <div>
        {/* Zone 1 + 2 + 3: Treys, KG Bagged & Sorting — one section */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <CartIconSvg size={18} color="#fb923c" />
              {_t("entered_completed_treys", "Entered / Completed Treys")}
            </span>
          }
        >
          {/* Treys */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, padding: "4px 0 16px" }}>
            {enteredCompletedTreysItems.map((item, i) => (
              <div key={i} className="db-metric-card">
                <div className="db-metric-label">{item.label}</div>
                <div className="db-metric-value">{item.value ?? 0}</div>
              </div>
            ))}
          </div>

          {/* KG Bagged + Sorting side by side */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {/* KG Bagged */}
            <div style={{ border: "1px solid #f0f1f3", borderRadius: 8, padding: 12 }}>
              <div className="db-avg-header" style={{ marginBottom: 10 }}>
                <span className="db-avg-header-title d-flex align-items-center gap-2">
                  <ScaleIconSvg size={14} color="#10b981" />
                  {_t("kg_bagged", "KG Bagged")}
                </span>
              </div>
              <div className="fqm-grid">
                {kgBaggedItems.map((item, i) => (
                  <div key={i} className="pp-brokering-box bro-box-green">
                    <div className="pp-brokering-box-label">{item.label}</div>
                    <div className="pp-brokering-box-value">{item.value ?? "0"}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sorting */}
            <div style={{ border: "1px solid #f0f1f3", borderRadius: 8, padding: 12 }}>
              <div className="db-avg-header" style={{ marginBottom: 10 }}>
                <span className="db-avg-header-title d-flex align-items-center gap-2">
                  <SortingIconSvg size={14} color="#f97316" />
                  {_t("sorting", "Sorting")}
                </span>
              </div>
              <div className="fqm-grid">
                {sortingItems.map((item, i) => (
                  <div key={i} className="pp-brokering-box bro-box-orange">
                    <div className="pp-brokering-box-label">{item.label}</div>
                    <div className="pp-brokering-box-value">{item.value ?? "0"}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DashboardSection>

        {/* Zone 4 + 5: Packing & Packing Turn 2 — one section */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <PackageIconSvg size={18} color="#fb923c" />
              {_t("packing_overview", "Packing Overview")}
            </span>
          }
        >
          {/* Packing Day Card */}
          <div className="cmn_card" style={{ overflow: "hidden", marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "#fff8f5", borderBottom: "1px solid #f0e8e0" }}>
              <span style={{ fontFamily: "'Public Sans', serif", fontWeight: 600, fontSize: 15, color: "#1d1f2c", display: "flex", alignItems: "center", gap: 8 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
                {_t("packing_day", "Packing")} 
              </span>
            </div>
            <div style={{ background: "#fff", padding: "0" }}>
              <div className="pko-grid-5">
                <MetricBox label="TOTAL BOXES" value={day?.total_boxes_packed ?? 0} theme="orange" />
                <MetricBox label="CHEEKS" value={day?.total_cheeks_10lb ?? 0} unit="10 LB" theme="orange" />
                <MetricBox label="CHEEKS" value={day?.total_cheeks_20lb ?? 0} unit="20 LB" theme="orange" />
                <MetricBox label="STRIPS" value={day?.total_strips_20lb ?? 0} unit="20 LB" theme="orange" />
                <MetricBox label="PASTING" value={day?.total_pasted_boxes_packed ?? 0} unit="20 LB" theme="orange" />
              </div>
            </div>
          </div>

          {/* Packing Turn 2 Card */}
          <div className="cmn_card" style={{ overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "#f0f1fb", borderBottom: "1px solid #d8daf5" }}>
              <span style={{ fontFamily: "'Public Sans', serif", fontWeight: 600, fontSize: 15, color: "#1d1f2c", display: "flex", alignItems: "center", gap: 8 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
                {_t("packing_turn_2", "Packing")}
              </span>
            </div>
            <div style={{ background: "#fff", padding: "0" }}>
              <div className="pko-turn2-layout">
                <div className="pko-total-cell pko-box-blue">
                  <div className="pko-box-label">TOTAL BOXES<br />(DAY & NIGHT)</div>
                  <div className="pko-box-value">
                    {((day?.total_boxes_packed ?? 0) + (night?.total_night_box_packed ?? 0)) || 0}
                  </div>
                </div>
                <div className="pko-grid-right">
                  <MetricBox label="TOTAL BOXES"         value={night?.total_night_box_packed ?? 0}  theme="blue" />
                  <MetricBox label="PRIME CHEEK BOXES"   value={night?.prime_cheeks_box_10lb ?? 0}   unit="10 LB" theme="blue" />
                  <MetricBox label="PRIME CHEEK BOXES"   value={night?.prime_cheeks_box_20lb ?? 0}   unit="20 LB" theme="blue" />
                  <MetricBox label="PRIME STRIP BOXES"   value={night?.prime_strips_box_20lb ?? 0}   unit="20 LB" theme="blue" />
                  <MetricBox label="SECONDS CHEEK BOXES" value={night?.second_cheeks_box_20lb ?? 0}  unit="20 LB" theme="blue" />
                  <MetricBox label="SECONDS STRIP BOXES" value={night?.second_strips_box_20lb ?? 0}  unit="20 LB" theme="blue" />
                  <MetricBox label="TAPITAS BOXES"       value={night?.tapita_box_20lb ?? 0}         unit="20 LB" theme="blue" />
                  <MetricBox label="PIECES BOXES"        value={night?.pieces_box_20lb ?? 0}         unit="20 LB" theme="blue" />
                </div>
              </div>
            </div>
          </div>
        </DashboardSection>

        {/* Zone 6: Metrics */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <BarChartIconSvg size={18} color="#60a5fa" />
              {_t("metrics", "Metrics")}
            </span>
          }
        >
          <DashboardCard title={_t("packing_metrics", "Packing Metrics")}>
            <div className="fqm-grid">
              {metricsItems.map((item, i) => (
                <div key={i} className="pp-brokering-box" style={{ background: "#fff", border: "1px solid #f0f1f3" }}>
                  <div className="pp-brokering-box-label">{item.label}</div>
                  <div className="pp-brokering-box-value">{formatValueWithUnit(item.label, item.value)}</div>
                </div>
              ))}
            </div>
          </DashboardCard>
        </DashboardSection>
      </div>
    </div>
  );
};

export default PackingDashboard;
